import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ThumbsUp, Heart, Lightbulb, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REACTIONS = [
  { type: "like", icon: ThumbsUp, label: "Like", activeColor: "text-[#1E88E5] bg-[#1E88E5]/10" },
  { type: "love", icon: Heart, label: "Love", activeColor: "text-red-500 bg-red-50 dark:bg-red-900/20" },
  { type: "insightful", icon: Lightbulb, label: "Insightful", activeColor: "text-amber-500 bg-amber-50 dark:bg-amber-900/20" },
  { type: "helpful", icon: HelpCircle, label: "Helpful", activeColor: "text-green-500 bg-green-50 dark:bg-green-900/20" },
];

export default function ReactionBar({ articleId }) {
  const { user } = useAuth();
  const [counts, setCounts] = useState({});
  const [userReactions, setUserReactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get(`/reactions/${articleId}`).then(r => {
      setCounts(r.data.counts || {});
      setUserReactions(r.data.user_reactions || []);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, [articleId]);

  const toggle = async (type) => {
    if (!user) { toast.error("Please login to react"); return; }
    setLoading(true);
    try {
      const res = await api.post("/reactions", { article_id: articleId, reaction_type: type });
      if (res.data.action === "added") {
        setCounts(prev => ({ ...prev, [type]: (prev[type] || 0) + 1 }));
        setUserReactions(prev => [...prev, type]);
      } else {
        setCounts(prev => ({ ...prev, [type]: Math.max(0, (prev[type] || 0) - 1) }));
        setUserReactions(prev => prev.filter(r => r !== type));
      }
    } catch { toast.error("Failed to react"); }
    setLoading(false);
  };

  const totalReactions = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-5" data-testid="reaction-bar">
      <p className="text-xs font-sans font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">
        {totalReactions > 0 ? `${totalReactions} reaction${totalReactions !== 1 ? "s" : ""}` : "React to this article"}
      </p>
      <div className="flex flex-wrap gap-2">
        {REACTIONS.map(r => {
          const active = userReactions.includes(r.type);
          const count = counts[r.type] || 0;
          return (
            <Button
              key={r.type}
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => toggle(r.type)}
              className={`font-sans text-xs gap-1.5 transition-all ${active ? r.activeColor + " border-current" : "hover:border-slate-400"}`}
              data-testid={`reaction-${r.type}`}
            >
              <r.icon className={`h-3.5 w-3.5 ${active ? "" : "text-muted-foreground"}`} />
              {r.label}
              {count > 0 && <span className="font-semibold">{count}</span>}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
